import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, DollarSign, Clock, Zap, Code, Target, Megaphone,
  Briefcase, Home, ArrowRight, Lock, Loader2, Send,
} from "lucide-react";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { capture } from "@/lib/analytics";

/**
 * /coach — AI Coaches page.
 *
 * Paid users: pick a coach (recommended ones surface first) and chat with
 * them. Free / anonymous users see the coach catalog with a locked paywall
 * card linking to /pricing.
 *
 * The chat respects the global no-template rule — coaches reply with
 * questions, hints, and fragment-level examples (never a copyable prompt).
 */

const ICON_MAP = {
  Sparkles, DollarSign, Clock, Zap, Code, Target, Megaphone,
  Briefcase, Home,
};

export default function CoachPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(searchParams.get("c") || null);

  useEffect(() => {
    let cancelled = false;
    apiClient.get("/coaches")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ coaches: [], recommended: [], is_paid: false, is_authed: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Fire a page-level view event once we know the user's paid status. Lets
  // the funnel separate "people who reached /coach" by paid vs free vs
  // anonymous so we can compute paywall conversion accurately.
  useEffect(() => {
    if (!data) return;
    capture("ai_coach_page_viewed", {
      is_paid: !!data.is_paid,
      is_authed: !!data.is_authed,
      recommended_count: (data.recommended || []).length,
    });
    if (!data.is_paid) {
      capture("ai_coach_paywall_viewed", { source: "coach_page" });
    }
  }, [data]);

  const selectCoach = (slug) => {
    setActiveSlug(slug);
    setSearchParams({ c: slug });
    capture("ai_coach_card_clicked", {
      coach_slug: slug,
      is_paid: !!data?.is_paid,
      recommended: (data?.recommended || []).includes(slug),
    });
  };

  if (loading) {
    return (
      <div className="App min-h-screen">
        <Header />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-20">
            <Loader2 size={32} className="animate-spin mx-auto text-[var(--primary,#2563EB)]" />
          </div>
        </main>
      </div>
    );
  }

  const isPaid = !!data?.is_paid;
  const coaches = data?.coaches || [];
  const recommended = data?.recommended || [];

  // Sort recommended coaches first.
  const sortedCoaches = [...coaches].sort((a, b) => {
    const ai = recommended.indexOf(a.slug);
    const bi = recommended.indexOf(b.slug);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const activeCoach = activeSlug ? coaches.find((c) => c.slug === activeSlug) : null;

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {!activeCoach && (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] mb-3"
                style={{ background: "rgba(37,99,235,0.10)", color: "var(--primary, #2563EB)" }}
              >
                <Sparkles size={11} /> Your AI Coach
              </div>
              <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight max-w-xl mx-auto">
                Get your personal AI learning coach
              </h1>
              <p className="mt-3 text-base text-[var(--ink-2)] leading-relaxed max-w-lg mx-auto">
                Your coach helps you stay on track, understand what to improve,
                and use AI for your real goals.
              </p>
            </div>

            {!isPaid && <PaywallCard authed={!!user} />}

            <div className="mt-8" data-testid="coach-catalog">
              {recommended.length > 0 && (
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--ink-3)] mb-3 px-1">
                  Recommended for you
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3" data-testid="coach-grid">
                {sortedCoaches.map((c) => (
                  <CoachCard
                    key={c.slug}
                    coach={c}
                    recommended={recommended.includes(c.slug)}
                    onSelect={() => {
                      if (isPaid) {
                        selectCoach(c.slug);
                      } else {
                        // Free users: record the intent + the coach they were
                        // eyeing so the funnel shows which coaches drive the
                        // most upgrade attempts, then route to pricing.
                        capture("ai_coach_card_clicked", {
                          coach_slug: c.slug,
                          is_paid: false,
                          recommended: recommended.includes(c.slug),
                        });
                        capture("ai_coach_paywall_upgrade_clicked", {
                          source: "coach_card",
                          coach_slug: c.slug,
                        });
                        window.location.href = "/pricing";
                      }
                    }}
                    locked={!isPaid}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {activeCoach && (
          <CoachChat coach={activeCoach} onBack={() => { setActiveSlug(null); setSearchParams({}); }} />
        )}
      </main>
    </div>
  );
}

function CoachCard({ coach, recommended, onSelect, locked }) {
  const Icon = ICON_MAP[coach.icon] || Sparkles;
  return (
    <button
      onClick={onSelect}
      className="brut p-5 text-left transition-colors hover:border-[var(--ink)] relative"
      style={recommended ? { borderColor: "rgba(37,99,235,0.45)", background: "rgba(37,99,235,0.03)" } : undefined}
      data-testid={`coach-card-${coach.slug}`}
    >
      {recommended && (
        <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--primary,#2563EB)]">
          For your goals
        </span>
      )}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--primary,#2563EB)] text-white flex items-center justify-center flex-shrink-0">
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base sm:text-lg leading-tight">{coach.name}</div>
          <p className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">{coach.tagline}</p>
          <div className="mt-3 text-sm font-semibold inline-flex items-center gap-1.5"
            style={{ color: locked ? "var(--ink-3)" : "var(--primary,#2563EB)" }}
          >
            {locked ? <><Lock size={12} /> Unlock</> : <>Talk to {coach.name.split(" ")[0]} <ArrowRight size={13} /></>}
          </div>
        </div>
      </div>
    </button>
  );
}

function PaywallCard({ authed }) {
  return (
    <div
      className="brut p-6 sm:p-7"
      style={{
        background: "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(124,58,237,0.06))",
        borderColor: "rgba(37,99,235,0.4)",
      }}
      data-testid="coach-paywall"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-[var(--primary,#2563EB)] text-white flex items-center justify-center flex-shrink-0">
          <Lock size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl sm:text-2xl leading-tight tracking-tight">
            Unlock your personal AI Coach
          </h2>
          <p className="text-sm sm:text-base text-[var(--ink-2)] mt-1.5 leading-relaxed">
            Get guided help for your real goals — sales, money, automation,
            content, and more. Your coach asks the right questions and helps
            you write stronger prompts in your own words.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/pricing"
              onClick={() => capture("ai_coach_paywall_upgrade_clicked", { source: "paywall_card" })}
              className="btn-duo inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm"
              data-testid="coach-paywall-upgrade"
            >
              Unlock AI Coaches <ArrowRight size={14} />
            </Link>
            {!authed && (
              <Link to="/skill-check" className="btn-ghost text-sm">
                Take the Skill Check first
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CoachChat({ coach, onBack }) {
  const Icon = ICON_MAP[coach.icon] || Sparkles;
  const [messages, setMessages] = useState([
    { role: "assistant", content: coach.welcome },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [err, setErr] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (text.length < 2 || sending) return;
    setErr(null);
    setInput("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setSending(true);
    capture("ai_coach_chat_sent", {
      coach_slug: coach.slug,
      turn_index: messages.filter((m) => m.role === "user").length + 1,
      message_chars: text.length,
    });
    try {
      const r = await apiClient.post("/coaches/chat", {
        coach_slug: coach.slug,
        message: text,
        session_id: sessionId,
        history: next.slice(-12),
      });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply || "..." }]);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        // Defensive: chat usually unreachable for free users since the card
        // redirects them — but if a subscription expires mid-session this
        // path fires. Capture it so we know mid-session expiries happen.
        capture("ai_coach_paywall_viewed", { source: "chat_402" });
        setErr("AI Coaches are a paid feature. Upgrade to Mastery to keep chatting.");
      } else {
        setErr(e?.response?.data?.detail || "Couldn't reach your coach. Try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="coach-chat">
      <button onClick={onBack} className="btn-ghost text-sm mb-4 inline-flex items-center gap-1.5" data-testid="coach-chat-back">
        ← Back to coaches
      </button>
      <div className="brut p-4 sm:p-5 mb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--primary,#2563EB)] text-white flex items-center justify-center flex-shrink-0">
          <Icon size={18} />
        </div>
        <div>
          <div className="font-display text-lg leading-tight">{coach.name}</div>
          <p className="text-xs text-[var(--ink-3)] mt-0.5">{coach.tagline}</p>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="brut p-4 sm:p-5 mb-3 overflow-y-auto"
        style={{ maxHeight: "60vh", minHeight: "420px" }}
        data-testid="coach-chat-messages"
      >
        {messages.map((m, i) => (
          <motion.div
            key={`m-${i}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className={`mb-4 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`coach-msg-${m.role}`}
          >
            <div
              className="max-w-[88%] sm:max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap"
              style={
                m.role === "user"
                  ? { background: "var(--ink,#0F1419)", color: "white" }
                  : { background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)", color: "var(--ink)" }
              }
            >
              {m.content}
            </div>
          </motion.div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="coach-typing">
            <div className="px-4 py-3 rounded-2xl text-sm" style={{ background: "rgba(37,99,235,0.06)" }}>
              <Loader2 size={14} className="animate-spin inline mr-1.5" />
              Thinking…
            </div>
          </div>
        )}
      </div>

      {err && (
        <div className="text-sm text-[var(--accent,#FF5C2A)] mb-3" data-testid="coach-chat-error">{err}</div>
      )}

      <div className="flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          maxLength={1000}
          placeholder="Ask your coach anything…"
          disabled={sending}
          className="brut-input flex-1 font-mono text-sm leading-relaxed resize-y disabled:opacity-60"
          data-testid="coach-chat-input"
        />
        <button
          onClick={send}
          disabled={sending || input.trim().length < 2}
          className="btn-duo inline-flex items-center justify-center gap-1.5 px-4 py-3 self-stretch"
          data-testid="coach-chat-send"
        >
          <Send size={14} />
        </button>
      </div>
      <p className="text-[11px] text-[var(--ink-3)] mt-2 leading-relaxed">
        Your coach will never write a finished prompt for you — they ask
        questions and give hints so you keep building real skill.
      </p>
    </div>
  );
}
